import {Component, EventEmitter, Input, OnChanges, OnInit, Output, TemplateRef} from '@angular/core';
import {CatalogItemModel, CatalogModel, ValuePropertyModel} from '../../../models/property.model';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {ModalService} from '../../../services/modal/modal.service';
import {CrmService} from '../../../services/api/crm.service';
import {AlertService} from '../../../services/notify/alert.service';
import {MemberModel} from '../../../models/user.model';
import {HelpersService} from '../../../services/helpers.service';
import {FileModel} from '../../../models/file.model';
import {DocumentsApiService} from '../../../services/api/documents-api.service';
import {MemberService} from '../../../services/member.service';
import {PropertiesService} from '../../../services/api/properties-service.service';
import {ContractsService} from '../../../services/api/contracts.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-detail-property',
    templateUrl: './detail-property.component.html',
    styleUrls: ['./detail-property.component.scss']
})
export class DetailPropertyComponent implements OnInit, OnChanges {
    @Input() customProperties: ValuePropertyModel[];
    @Input() target: 'board' | 'contract' | 'file' | 'collection' | 'folder' = 'board';
    @Input() targetKey: string | number;
    @Input() canEdit = true;

    @Output() refreshEmit: EventEmitter<void> = new EventEmitter<void>();

    emptyPropertiesModal: NgbModalRef;

    allMembers: MemberModel[];
    copyMembers: MemberModel[];
    currentMember: MemberModel;

    editingProperty: ValuePropertyModel;
    modalSize: 'lg' | 'xl' | 'sm' = 'xl';

    docData: FileModel;

    allPropsShow: boolean;

    selectedCatalog: CatalogModel;

    newCatalogItemText = '';

    constructor(
        public modalService: ModalService,
        private crmService: CrmService,
        private alertService: AlertService,
        public helper: HelpersService,
        public docApi: DocumentsApiService,
        private membersService: MemberService,
        private propertyService: PropertiesService,
        private contractApi: ContractsService,
        public translate: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.membersService.getAllMembers().then(res => {
            this.allMembers = res;
            this.copyMembers = structuredClone(res);
        });

        this.membersService.getCurrentMember().then(res => {
            this.currentMember = res;
        });
    }

    ngOnChanges(): void {
        this.prepareCustomProperties();
    }

    prepareCustomProperties(): void {
        this.customProperties = this.customProperties.map(prop => {
            if (['property_member', 'property_file', 'property_catalog'].includes(prop.property.property_type)) {
                if (prop.property.is_multiform && prop.property_values && !prop.property_values.length) {
                    prop.property_values = null;
                }
                if (!prop.property.is_multiform && prop.property_values && !(prop.property_values instanceof Array)) {
                    prop.property_values = [prop.property_values];
                }
            } else if (prop.property.property_type === 'property_todo' && prop.property_values.length) {
                prop.catalog.items = prop.catalog.items.filter(x => {
                    if (!prop.property_values.find(item => item.id === x.id)) {
                        return x;
                    }
                });
            }
            return prop;
        });

        this.allPropsShow = this.customProperties.filter(x => x.default_view ||
            x.property_values).length !== this.customProperties.length;


        if (!!this.emptyPropertiesModal &&
            !this.customProperties.filter(prop =>
                !prop.default_view && !prop.property_values).length
        ) {
            this.emptyPropertiesModal.close();
            this.emptyPropertiesModal = undefined;
        }
    }

    openConfirmModal(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            const modalSettings: any = {
                modalHeader: this.translate.instant('CONFIRM.HEADERS.ATTENTION'),
                modalBody: this.translate.instant('CONFIRM.TEXT.CHANGES'),
                buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
                buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
            };
            const confirmModal = this.modalService.createFromSettings(modalSettings);
            confirmModal.result.then(data => {
                if (data) {
                    resolve(true);
                } else {
                    reject(false);
                }
            }).catch(() => {
                reject(false);
            });
        });
    }

    openPropertyEditor(property: ValuePropertyModel, content: TemplateRef<any>, size: 'lg' | 'xl' | 'sm' = 'xl'): void {
        if (!this.canEdit || (property.property.property_type === 'property_catalog' && !property.property.property_type_target)) {
            return;
        }
        if (property.property_values) {
            this.openConfirmModal().then(() => {
                this.openEditorModal(property, content, size);
            }).catch(() => {
                return;
            });
        } else {
            this.openEditorModal(property, content, size);
        }
    }

    clearPropertyValue(property: ValuePropertyModel): void {
        this.openConfirmModal().then(() => {
            this.editingProperty = property;
            this.setProperty(null);
        }).catch(() => {
            return;
        });
    }

    openEditorModal(property: ValuePropertyModel, content: TemplateRef<any>, size: 'lg' | 'xl' | 'sm' = 'xl'): void {
        this.modalSize = size;
        this.editingProperty = structuredClone(property);
        this.selectedCatalog = null;
        switch (property.property.property_type) {
            case 'property_catalog' :
                this.propertyService.getCatalogById(Number(property.property.property_type_target)).subscribe({
                    next: res => {
                        this.selectedCatalog = res.catalog;
                        this.selectedCatalog.items = this.selectedCatalog.items.filter(item => {
                            if (this.editingProperty.property_values) {
                                if (!this.editingProperty.property_values.filter((x: CatalogItemModel) => x.id === item.id)[0]) {
                                    return item;
                                }
                            } else {
                                return item;
                            }
                        });
                    }
                });
                break;

            case 'property_member':
                this.allMembers = structuredClone(this.copyMembers);
                switch (property.property.property_type_target) {
                    case 'inner':
                        this.allMembers = this.allMembers.filter(x => x.project_setting.observation_type === 'inner');
                        break;
                    case 'outer':
                        this.allMembers = this.allMembers.filter(x => x.project_setting.observation_type === 'outer');
                        break;
                }
                if (this.editingProperty.property_values) {
                    this.allMembers = this.allMembers.filter(item => {
                        if (this.editingProperty.property_values.filter((x: MemberModel) => x.guid !== item.guid).length) {
                            return item;
                        }
                    });
                }
                break;
        }
        this.modalService.openTemplateModal(content, size);
    }

    changeCheckInTodo(property: ValuePropertyModel, item: CatalogItemModel): void {
        this.editingProperty = property;
        this.openConfirmModal().then(() => {
            const checkedItem = this.editingProperty.property_values.find((x: CatalogItemModel) => x.id === item.id);
            if (checkedItem) {
                this.editingProperty.property_values = this.editingProperty.property_values
                    .filter((x: CatalogItemModel) => x.id !== item.id);
                // this.editingProperty.catalog.items.push(checkedItem);
            } else {
                const uncheckedItem = this.editingProperty.catalog.items.find(x => x.id === item.id);
                this.editingProperty.property_values.push(uncheckedItem);
                // this.editingProperty.catalog.items = this.editingProperty.catalog.items.filter(x => x.id !== uncheckedItem.id);
            }

            this.setProperty(this.editingProperty.property_values);
        }).catch(() => {
            return;
        });
    }

    setProperty(value: any, modal?: NgbModalRef): void {
        if (value &&
            ['property_member', 'property_catalog', 'property_file', 'property_todo'].includes(this.editingProperty.property.property_type)
        ) {
            const targetKeys = {
                property_member: 'guid',
                property_catalog: 'id',
                property_file: 'key',
                property_todo: 'id',
            };
            const currentTargetKey = targetKeys[this.editingProperty.property.property_type];
            value = this.editingProperty.property_values.map((x: any) => {
                return x[currentTargetKey];
            }).toString();
        }
        switch (this.target) {
            case 'board':
                this.crmService.setOrUpdateProperty(this.targetKey, this.editingProperty.property.id, {value}).subscribe({
                    next: () => {
                        if (!value) {
                            this.editingProperty.property_values = null;
                        }
                        this.responseHandler(modal);
                    }, error: err => {
                        this.alertService.setAlert(err.message, 'blue');
                    }
                });
                break;

            case 'contract':
                this.contractApi.setOrUpdateProperty(this.targetKey as string, this.editingProperty.property.id, {value}).subscribe({
                    next: () => {
                        if (!value) {
                            this.editingProperty.property_values = null;
                        }
                        this.responseHandler(modal);
                    }, error: err => {
                        this.alertService.setAlert(err.message, 'blue');
                    }
                });
                break;

            default: // target === 'file' | 'collection' | 'folder'
                this.docApi.setOrUpdateProperty(this.target, this.targetKey, this.editingProperty.property.id, {value}).subscribe({
                    next: () => {
                        if (!value) {
                            this.editingProperty.property_values = null;
                        }
                        this.responseHandler(modal);
                    }, error: err => {
                        this.alertService.setAlert(err.message, 'blue');
                    }
                });
                break;
        }
    }

    addCatalogItem(): void {
        const data = {
            catalog_id: this.selectedCatalog.id,
            items_text: this.newCatalogItemText
        };
        this.propertyService.createCatalogItem(data).subscribe({
            next: res => {
                if (!this.editingProperty.property_values) {
                    this.editingProperty.property_values = [];
                }
                if (this.editingProperty.property.is_multiform) {
                    this.editingProperty.property_values.push(res.catalog_item);
                } else {
                    this.editingProperty.property_values = [res.catalog_item];
                }
                this.newCatalogItemText = '';
                this.alertService.setAlert(res.message, 'green');
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    responseHandler(modal?: NgbModalRef): void {
        this.customProperties = this.customProperties.map(x => {
            if (x.property.id === this.editingProperty.property.id) {
                x = this.editingProperty;
                return x;
            } else {
                return x;
            }
        });
        this.refreshEmit.emit();
        if (modal) {
            modal.close();
        }
    }

    openDoc(value: any, docModal: TemplateRef<any>): void {
        this.docData = value;
        this.modalService.openTemplateModal(docModal, 'xl');
    }

    deleteItemOfPropertyValues(currentValue: any): void {
        const targetKeys = {
            property_member: 'guid',
            property_catalog: 'id',
            property_file: 'key',
        };
        const currentTargetKey = targetKeys[this.editingProperty.property.property_type];

        if (currentTargetKey === 'guid') {
            this.allMembers.push(currentValue);
        } else if (currentTargetKey === 'id') {
            this.selectedCatalog.items.push(currentValue);
        }

        this.editingProperty.property_values = this.editingProperty.property_values.filter((value: any) =>
            value[currentTargetKey] !== currentValue[currentTargetKey]);

        if (!this.editingProperty.property_values.length) {
            this.editingProperty.property_values = null;
        }
    }

    addValueToPropertyValues(key: string): void {
        if (key === 'newItem') {
            return;
        }
        if (!this.editingProperty.property_values) {
            this.editingProperty.property_values = [];
        }

        if (this.editingProperty.property.property_type === 'property_member') {
            if (this.editingProperty.property.is_multiform) {
                this.editingProperty.property_values.push(this.allMembers.find(x => x.guid === key));
                this.allMembers = this.allMembers.filter(x => x.guid !== key);
            } else {
                this.editingProperty.property_values = [this.allMembers.find(x => x.guid === key)];
            }
        } else {
            if (this.editingProperty.property.is_multiform) {
                this.editingProperty.property_values.push(this.selectedCatalog.items.find(x => x.id.toString() === key));
                this.selectedCatalog.items = this.selectedCatalog.items.filter(x => x.id.toString() !== key);
            } else {
                this.editingProperty.property_values = [this.selectedCatalog.items.find(x => x.id.toString() === key)];
            }
        }
    }
}
